import { Link } from "@/lib/link";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import SEO from "@/components/common/SEO";
import PageHero from "@/components/common/PageHero";
import ScrollReveal from "@/components/common/ScrollReveal";
import { productCategories } from "@/data/products";

export default function Products() {
    const { t } = useTranslation();

    const schema = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListElement: productCategories.map((c, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: t(c.nameKey),
            url: `https://exportfy.world/products/${c.slug}`,
        })),
    };

    return (
        <>
            <SEO
                title="Export Product Categories — Textiles, Safety, Agro | Exportfy.world"
                description="Explore Exportfy.world's six export categories: towels, home textiles, handmade rugs & carpets, uniform fabrics, industrial safety products, and agro products. OEM and private label available."
                path="/products"
                schema={schema}
            />
            <PageHero
                eyebrow={t("productsPage.hero.eyebrow")}
                title={t("productsPage.hero.title")}
                subtitle={t("productsPage.hero.subtitle")}
            />
            <section className="section-y">
                <div className="container-x">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {productCategories.map((c, i) => (
                            <ScrollReveal key={c.slug} delay={i * 60}>
                                <Link to={`/products/${c.slug}`} data-testid={`product-card-${c.slug}`} className="group relative block bg-white rounded-2xl overflow-hidden border border-slate-200 transition-all duration-500 hover:shadow-[0_20px_40px_rgba(10,37,64,0.10)] hover:-translate-y-1">
                                    <div className="aspect-[4/3] overflow-hidden">
                                        <img src={c.image} alt={t(c.nameKey)} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                    </div>
                                    <div className="p-6">
                                        <div className="flex items-center justify-between">
                                            <h3 className="text-xl font-bold text-[#0A2540]">{t(c.nameKey)}</h3>
                                            <ArrowRight className="w-4 h-4 text-[#1D4ED8] transition-transform group-hover:translate-x-1" />
                                        </div>
                                        <p className="mt-2 text-sm text-slate-500 leading-relaxed">{t(c.taglineKey)}</p>
                                        <div className="mt-4 flex flex-wrap gap-2">
                                            {c.oem && <span className="text-[10px] font-semibold uppercase tracking-widest text-emerald-700 bg-emerald-50 rounded-full px-2.5 py-1">{t("products.badges.oem")}</span>}
                                            {c.privateLabel && <span className="text-[10px] font-semibold uppercase tracking-widest text-amber-700 bg-amber-50 rounded-full px-2.5 py-1">{t("products.badges.privateLabel")}</span>}
                                        </div>
                                    </div>
                                </Link>
                            </ScrollReveal>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
}
